import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import SmallSpinner from "../loading/SmallSpinner";
import Icon1 from "../icons/Icon";

type DropDownItem = {
  value: string | number;
  label: string;
  extra_label?: string;
  type?: string;
  client_type?: string; // Optional field for client type
};

interface DropDownGPTS3Props {
  label: string;
  disabled?: boolean;
  items?: DropDownItem[];
  value?: string | number;
  onChange?: (item: any) => void;
  onSearchChange?: (query: string) => void;
  onFetchNewOptions?: () => void;
  loadingSearch?: boolean;
  className?: string;
  mandatory?: boolean;
  debounceDelay?: number;
  hasMoreOptions?: boolean;
}

function DropDownGPTS3({
  label,
  disabled = false,
  items = [],
  value = "",
  onChange = () => {},
  onSearchChange = () => {},
  onFetchNewOptions = () => {}, // Function to fetch more options
  loadingSearch = false,
  className = "",
  mandatory = false,
  debounceDelay = 500, // Default 500ms delay
  hasMoreOptions = false, // Flag to indicate if more options are available
}: DropDownGPTS3Props) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focus, setHasFocus] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLInputElement>(null);
  const listRef = useRef(null);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If there's more space above than below, open upwards
      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 300;
      setOpenUpwards(shouldOpenUpwards);

      // Set position relative to viewport
      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if the click is on the dropdown list (portal content)
        const dropdownList = document.querySelector("[data-dropdown-portal]");
        if (!dropdownList || !dropdownList.contains(event.target)) {
          setIsOpen(false);
          setHasFocus(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Filter items based on the input query
  const filteredItems = items.filter((item) =>
    item.label?.toLowerCase().includes(query?.toLowerCase())
  );

  let has_value = value !== null && value !== undefined && value !== "";
  let disabled_classname =
    " outline-none ring-transparent border-neutral-300 border-1 bg-neutral-100 border-neutral-300 text-shadow_25";

  const handleSelect = (e: any, item: DropDownItem) => {
    e.stopPropagation(); // Prevents parent click event from firing

    onChange(item);
    setQuery(""); // Clear query after selection
    setIsOpen(false);
    setHasFocus(false);
  };

  const handleInputChange = (e: any) => {
    if (disabled) return;
    setQuery(e.target.value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange && onSearchChange(e.target.value);
    }, debounceDelay);

    if (value) {
      onChange({
        target: {
          value: null,
        },
      }); // Reset selected value if a new query is being typed
    }
  };

  // Handle list scrolling to detect when user is near the end
  const handleListScroll = (e: any) => {
    if (!hasMoreOptions || loadingSearch) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // If we're within 20px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 20) {
      onFetchNewOptions();
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Portal dropdown content
  const dropdownContent =
    isOpen && !disabled ? (
      <ul
        ref={listRef}
        data-dropdown-portal="true"
        onScroll={handleListScroll}
        style={{
          position: "fixed",
          top: openUpwards ? dropdownPosition.top - 300 : dropdownPosition.top, // Adjust height as needed
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999, // Very high z-index to ensure it's on top
          maxHeight: "300px",
        }}
        className="gap-1
      
        flex flex-col p-2 border-1 pointer-events-auto border-shadow_10 bg-white shadow-xl h-auto rounded-lg text-base ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
      >
        {filteredItems.map((item, index) => (
          <li
            key={index}
            className={`cursor-pointer select-none relative rounded-lg py-2 pl-3 pr-9 ${
              value !== null && value === item.value
                ? "text-indigo-500 bg-blue-50"
                : "text-gray-900 hover:bg-indigo-10"
            }`}
            onClick={(e) => handleSelect(e, item)}
          >
            <div className="flex items-center">
              <span
                className={`block truncate ${
                  value !== null && value === item.value ? "font-semibold" : ""
                }`}
              >
                {item.label}{" "}
                {item.extra_label && (
                  <span
                    className={`${
                      item.client_type
                        ? item.client_type === "particulier"
                          ? "text-purple-400 bg-purple-50"
                          : item.client_type === "constructor"
                          ? "text-indigo-400 bg-indigo-50"
                          : "text-blue-400 bg-blue-50"
                        : "text-blue-400 bg-blue-50"
                    } font-light rounded-full px-1 text-h4 ml-1`}
                  >
                    {item.extra_label}
                  </span>
                )}
              </span>
            </div>
            {value !== null && value === item.value ? (
              <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                <svg
                  className="h-5 w-5 text-indigo-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 5.707 8.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            ) : null}
          </li>
        ))}
        {filteredItems.length === 0 && !loadingSearch && (
          <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
            Aucun résultat trouvé
          </li>
        )}
        {loadingSearch && (
          <li className="cursor-default select-none relative flex flex-row gap-2 py-2 pl-3 pr-9 text-gray-500">
            Récupération <SmallSpinner size={20} loading={true} />
          </li>
        )}
      </ul>
    ) : null;

  return (
    <div
      tabIndex={0}
      onBlur={() => {
        setTimeout(() => {
          if (isOpen) {
            setIsOpen(false);
            setHasFocus(false);
          }
        }, 200);
      }}
      onClick={(e) => {
        isOpen ? setIsOpen(false) : !disabled && setIsOpen(true);
      }}
      className={`relative   pointer-events-none select-none outline-none bg-green-200 w-full flex ${
        has_value ? "items-end py-1" : "items-center py-1"
      } min-h-11 h-11 rounded-xl shadow-sm ${
        disabled ? disabled_classname : value ? "bg-white" : "bg-transparent"
      } sm:text-sm ${
        isOpen || disabled ? "cursor-default" : "cursor-pointer"
      } ${
        !disabled && isOpen
          ? "border-2 outline-1 outline-blue-100 border-blue-300"
          : mandatory && !has_value
          ? "border-red-300 border-2 outline-1 outline-red-100"
          : "border"
      }`}
    >
      <div className="w-full">
        {has_value && (
          <div
            className={`absolute top-0 px-2 text-h6 ${
              isOpen ? "text-blue-400" : "text-gray-300"
            }`}
          >
            {label}
          </div>
        )}
        {mandatory && !has_value && !isOpen && (
          <div className="absolute top-0 justify-start h-full items-start flex py-0 flex-row right-8 text-white text-h6">
            <div className="bg-red-300 flex flex-row items-end rounded-b-lg px-1 h-5 pb-0.5">
              Champ obligatoire
            </div>
          </div>
        )}
        {!isOpen && (
          <div className="absolute top-1/3 right-3">
            <Icon1
              icon_name="down"
              size={15}
              fill={mandatory && !has_value && !isOpen ? "red" : "grey"}
            />
          </div>
        )}
        {isOpen && (
          <div className="absolute top-1/3 right-3">
            <Icon1 icon_name="up" size={15} fill="grey" />
          </div>
        )}
        <input
          ref={dropdownRef}
          type="text"
          className="w-full rounded-xl pointer-events-auto px-2 outline-none transition ease-in-out duration-150"
          placeholder={label}
          value={
            value !== null
              ? items.find((item) => item.value === value)?.label
              : query
          }
          disabled={disabled}
          onChange={handleInputChange}
          autoComplete="zzz"
        />
      </div>

      {/* Render dropdown in portal to escape parent constraints */}
      {typeof window !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
}

export default DropDownGPTS3;
